import { AxiosError } from 'axios';
import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { IResponseMessages } from '../models/response-messages.model';
import { NewReadOnlyClient } from '../clients/new-read-only.client';
import { IBaseModel, ListModel, ListQueryParam } from '../models';

export type UseListOptions<ResponseModel extends IBaseModel> = Omit<
    UseQueryOptions<ListModel<ResponseModel>, AxiosError>,
    'queryKey'
> & { messages?: IResponseMessages };

export type UseListParams<ResponseModel extends IBaseModel, QueryModel extends ListQueryParam = ListQueryParam> = [
    QueryModel,
    UseListOptions<ResponseModel>?,
];

export function useList<ResponseModel extends IBaseModel, QueryModel extends ListQueryParam = ListQueryParam>(
    client: NewReadOnlyClient<ResponseModel, QueryModel>,
    listKey: string,
    [query, options]: UseListParams<ResponseModel, QueryModel>,
): UseQueryResult<ListModel<ResponseModel>, AxiosError> {
    return useQuery<ListModel<ResponseModel>, AxiosError>({
        queryKey: [listKey, query],
        queryFn: () => client.getList(query, options?.messages),
        ...options,
    });
}

export type UseItemOptions<ResponseModel extends IBaseModel> = Omit<
    UseQueryOptions<ResponseModel, AxiosError>,
    'queryKey'
> & { messages?: IResponseMessages };

export type UseItemParams<ResponseModel extends IBaseModel> = [string?, UseItemOptions<ResponseModel>?];

export function useItem<ResponseModel extends IBaseModel, QueryModel extends ListQueryParam = ListQueryParam>(
    client: NewReadOnlyClient<ResponseModel, QueryModel>,
    itemKey: string,
    [id, options]: UseItemParams<ResponseModel>,
): UseQueryResult<ResponseModel, AxiosError> {
    return useQuery<ResponseModel, AxiosError>({
        queryKey: [itemKey, id],
        queryFn: () => client.getOne(id, options?.messages),
        enabled: !!id,
        ...options,
    });
}
